import * as React from 'react';
import { Row, Col, Select, Button, Icon, Divider } from 'antd';
import I18N from 'src/services/I18NService';
import { IDrugStoreInfo } from 'src/entity/DrugStoreEntity';
import { IDoctorSellerSetting } from 'src/entity/DoctorEntity';
import { IMallPharmacyDetail, ISettingPharmacySource, IMallPharmacySettingInfo } from 'src/entity/MallEntity';
import { fetchPharmacyListBySellerUser } from 'src/redux/actions/mallPharmacyCreator';
import PharmacyCascader from './PharmacyCascader';
import * as uuid from 'uuid';
import { validateRegionError } from 'src/redux/actions/drugStoreCreator';
import { EnumMedicineCategory } from 'src/constants/enum';
import { IAgentInfo } from 'src/entity/AgentEntity';

interface IPharmacySelectProps {
  /** 是否是代理商 */
  isAgent: boolean;
  /** 是否是编辑状态 */
  isEdit: boolean;
  /** 是否显示继承药库按钮 */
  showExtend: boolean;
  /** 用户详情 */
  userInfo: IAgentInfo | IDrugStoreInfo;
  /** 云药房 */
  isCloud: boolean;
  /** 是否在线订购, 仅代理商并且医生有配药中心数据 */
  isOrderOnline: boolean;
  /** 供应商列表-代理商的配药中心 */
  sellerList: Array<IAgentInfo | IDrugStoreInfo>;
  /** 代理商生效的单元药库 */
  pharmacyList: IMallPharmacyDetail[];
  /** 代理商的配药中心 */
  drugStoreList: IDrugStoreInfo[];
  /** 本地药房下拉选项 */
  localSettingData: IMallPharmacySettingInfo[];
  /** 医生职业地址省市区id数组 */
  locationChain: number[];
  /** 唯一标识 */
  uniqueKey: string;
  /** 传入的Seller数据 */
  dataSellerSetting: Partial<IDoctorSellerSetting>;
  /** 传入的单元药库数据， 需要根据配药中心或者代理商的seller_user过滤单元药库列表 */
  dataPharmacyList: ISettingPharmacySource[];
  /** 配药中心设置数据新增回调事件 */
  cbDataAdd(key: string): void;
  /** 配药中心设置数据变更回调事件 */
  cbDataChange(key: string, sellerData: IDoctorSellerSetting | undefined, pharmacyList: ISettingPharmacySource[]): void;
  /** 配药中心设置数据删除回调事件 */
  cbDataDelete(key: string): void;
}

interface IPharmacySelectState {
  /** 医生职业地址是否在配药中心范围内（开通在线订购） */
  regionError: boolean;
  /** 代理商(未开通在线订购)或配药中心（开通在线订购）生效的单元药库 */
  pharmacyList: IMallPharmacyDetail[];
}

class PharmacySelectBox extends React.Component<IPharmacySelectProps, IPharmacySelectState> {
  constructor(props: IPharmacySelectProps) {
    super(props);

    this.state = {
      regionError: false,
      pharmacyList: [],
    };
  }

  async componentDidMount() {
    const { isOrderOnline, isEdit, isAgent, isCloud, dataSellerSetting, sellerList, pharmacyList, drugStoreList } = this.props;

    let regionError = false;
    const sellerUser = dataSellerSetting.seller_user;

    if (!!isOrderOnline && !!isEdit && !!isAgent && !!isCloud) {
      const drugStoreObj = (sellerList as IDrugStoreInfo[]).find((t) => t.seller_user === sellerUser);
      if (!!drugStoreObj) {
        const regions = drugStoreObj.regions || [];
        const locationChain = this.props.locationChain || [];
        regionError = validateRegionError(regions, locationChain);
      }
    }

    // 下拉选择的药库列表
    let tempPharmacyList: IMallPharmacyDetail[] = [];
    if (!!sellerUser) {
      if (isOrderOnline) {
        if (drugStoreList.filter((t) => t.is_active && t.is_online).findIndex((t) => t.seller_user === sellerUser) > -1) {
          // 配药中心的单元药库列表
          tempPharmacyList = await fetchPharmacyListBySellerUser(sellerUser);
        }
      } else {
        // 代理商的单元药库列表
        tempPharmacyList = pharmacyList.filter((t) => t.seller_user === sellerUser);
      }
    }

    this.setState({
      regionError,
      // pharmacyList: tempPharmacyList.filter((t) => t.category_code === EnumMedicineCategory.中药),
      pharmacyList: tempPharmacyList,
    });
  }

  componentWillUnmount() {
    this.setState = () => {
      return;
    };
  }

  /**
   * 配药中心变更时触发
   * 1、校验服务范围
   * 2、重置药库选择列表
   * 3、通知父级组件刷新数据
   * @memberof PharmacySelect
   */
  onChangeDrugStore = async (sellerUUID: string | undefined) => {
    const { sellerList, uniqueKey, isCloud } = this.props;
    if (!!sellerUUID) {
      const drugStoreObj = (sellerList as IDrugStoreInfo[]).find((t) => t.seller_user === sellerUUID);
      if (!!drugStoreObj && !!drugStoreObj.seller_user) {
        // 获取配药中心的单元药库
        const pharmacyList = await fetchPharmacyListBySellerUser(drugStoreObj.seller_user);
        // 验证选中配药中心服务范围是否包含医生职业地址
        const regions = drugStoreObj.regions || [];
        const locationChain = this.props.locationChain || [];
        const regionError = !!isCloud ? validateRegionError(regions, locationChain) : false;
        /** 选中的配药中心数据 */
        const sellerObj: IDoctorSellerSetting = {
          seller_user: drugStoreObj.seller_user,
          name: drugStoreObj.name,
        };

        this.setState({
          regionError, // 1、校验服务范围
          // pharmacyList: pharmacyList.filter((t) => t.category_code === EnumMedicineCategory.中药), // 2、重置药库选择列表
          pharmacyList, // 2、重置药库选择列表
        });

        // 3、通知父级组件刷新数据
        this.props.cbDataChange(uniqueKey, sellerObj, [
          {
            uniqueKey: uuid.v4(),
            medicine_group: undefined,
            medicine_price_policy: undefined,
            source: undefined,
          },
        ]);
      }
    }
  };

  /**
   * @description 代理商变更时触发
   * @memberof PharmacySelectBox
   */
  onChangeAgent = async (sellerUUID: string | undefined) => {
    const { userInfo, uniqueKey } = this.props;
    if (!!sellerUUID && !!userInfo.seller_user && userInfo.seller_user === sellerUUID) {
      // 获取配药中心的单元药库
      const pharmacyList = await fetchPharmacyListBySellerUser(userInfo.seller_user);
      /** 选中的配药中心数据 */
      const sellerObj: IDoctorSellerSetting = {
        seller_user: userInfo.seller_user,
        name: userInfo.name,
      };

      this.setState({
        // pharmacyList: pharmacyList.filter((t) => t.category_code === EnumMedicineCategory.中药), // 2、重置药库选择列表
        pharmacyList, // 2、重置药库选择列表
      });

      // 3、通知父级组件刷新数据
      this.props.cbDataChange(uniqueKey, sellerObj, [
        {
          uniqueKey: uuid.v4(),
          medicine_group: undefined,
          medicine_price_policy: undefined,
          source: undefined,
        },
      ]);
    }
  };

  /**
   * 删除配药中心设置时触发
   *
   * @memberof PharmacySelectBox
   */
  onRemoveDrugStore = () => {
    this.props.cbDataDelete(this.props.uniqueKey);
  };

  /**
   * 药库及价格模式发生改变时触发
   * 通知父级组件刷新数据
   * @memberof PharmacySelectBox
   */
  onChangePharmacyData = (data: ISettingPharmacySource) => {
    // 通知父级组件刷新数据
    const { uniqueKey, dataSellerSetting, dataPharmacyList } = this.props;
    const tempData = dataPharmacyList.find((t) => t.uniqueKey === data.uniqueKey);
    if (!!tempData) {
      tempData.medicine_group = data.medicine_group;
      tempData.medicine_price_policy = data.medicine_price_policy;
      tempData.stocking_inherit_from = data.stocking_inherit_from;
      tempData.source = data.source;
    }
    let sellerObj: IDoctorSellerSetting | undefined;
    if (!!dataSellerSetting && !!dataSellerSetting.seller_user) {
      sellerObj = {
        seller_user: dataSellerSetting.seller_user,
        name: dataSellerSetting.name || '',
      };
    }
    this.props.cbDataChange(uniqueKey, sellerObj, dataPharmacyList);
  };

  /**
   * 药库及价格模式数据删除时触发
   * 通知父级组件刷新数据
   * @memberof PharmacySelectBox
   */
  onRemovePharmacyData = (childKey: string) => {
    // 通知父级组件刷新数据
    const { uniqueKey, dataSellerSetting, dataPharmacyList } = this.props;
    const index = dataPharmacyList.findIndex((t) => t.uniqueKey === childKey);
    if (index > -1) {
      dataPharmacyList.splice(index, 1);
    }
    let sellerObj: IDoctorSellerSetting | undefined;
    if (!!dataSellerSetting && !!dataSellerSetting.seller_user) {
      sellerObj = {
        seller_user: dataSellerSetting.seller_user,
        name: dataSellerSetting.name || '',
      };
    }

    this.props.cbDataChange(uniqueKey, sellerObj, dataPharmacyList);
  };

  /**
   * 配药中心增加药库设置
   *
   * @memberof PharmacySelectBox
   */
  onAddPharmacyData = () => {
    const { uniqueKey, dataPharmacyList } = this.props;
    if (dataPharmacyList.length > 0) {
      const lastPharmacy = dataPharmacyList[dataPharmacyList.length - 1];
      if (!!lastPharmacy.medicine_group) {
        this.props.cbDataAdd(uniqueKey);
      }
    } else {
      this.props.cbDataAdd(uniqueKey);
    }
  };

  getOnlineSupplierSelect = (): JSX.Element => {
    const { isEdit, sellerList, dataSellerSetting } = this.props;
    return (
      <Select
        disabled={!isEdit}
        value={!!dataSellerSetting ? dataSellerSetting.seller_user : undefined}
        onChange={this.onChangeDrugStore}
        placeholder={`${I18N.get('common_choose')}${I18N.get('nav_drugStore')}`}
      >
        {(sellerList as IDrugStoreInfo[]).map((d) => {
          return (
            <Select.Option key={uuid.v4()} value={d.seller_user} disabled={!d.is_online || !d.is_active}>
              {!d.is_online ? `[${I18N.get('common_off_line')}]${d.name}` : d.is_active ? d.name : `[${I18N.get('common_status_disable')}]${d.name}`}
            </Select.Option>
          );
        })}
        {!!dataSellerSetting && (sellerList as IDrugStoreInfo[]).findIndex((t: IDrugStoreInfo) => t.seller_user === dataSellerSetting.seller_user) < 0 && (
          <Select.Option key={uuid.v4()} value={dataSellerSetting.seller_user} disabled={true}>
            {dataSellerSetting.name}
          </Select.Option>
        )}
      </Select>
    );
  };

  getOfflineSupplierSelect = (): JSX.Element => {
    const { isEdit, sellerList, dataSellerSetting, userInfo } = this.props;
    const sellerUser = (!!dataSellerSetting ? dataSellerSetting.seller_user : undefined) || userInfo.seller_user;
    return (
      <Select disabled={!isEdit} value={sellerUser} onChange={this.onChangeAgent} placeholder={`${I18N.get('common_choose')}${I18N.get('nav_drugStore')}`}>
        {(sellerList as IAgentInfo[]).map((d) => {
          return (
            <Select.Option key={uuid.v4()} value={d.seller_user}>
              {d.name}
            </Select.Option>
          );
        })}
        {/* {!!dataSellerSetting && (sellerList as IAgentInfo[]).findIndex((t: IAgentInfo) => t.seller_user === dataSellerSetting.seller_user) < 0 && (
          <Select.Option key={uuid.v4()} value={dataSellerSetting.seller_user} disabled={true}>
            {dataSellerSetting.name}
          </Select.Option>
        )} */}
        {!!dataSellerSetting && dataSellerSetting.seller_user !== userInfo.seller_user && (
          <Select.Option key={dataSellerSetting.seller_user} value={dataSellerSetting.seller_user} disabled={true}>
            {dataSellerSetting.name}
          </Select.Option>
        )}
      </Select>
    );
  };

  render() {
    const { isOrderOnline, isEdit, isAgent, dataPharmacyList, isCloud, showExtend, localSettingData } = this.props;
    const { regionError, pharmacyList } = this.state;
    // 生成药库级联组件
    const pharmacySelectList = dataPharmacyList.map((pharmacyObj, index) => {
      return (
        <div key={pharmacyObj.uniqueKey}>
          <PharmacyCascader
            key={pharmacyObj.uniqueKey}
            uniqueKey={`${pharmacyObj.uniqueKey}`}
            isEdit={isEdit}
            showExtend={showExtend}
            showRemove={dataPharmacyList.length > 1}
            isOrderOnline={isOrderOnline}
            isCloud={isCloud}
            pharmacyList={pharmacyList.filter(
              (t) =>
                dataPharmacyList
                  .filter((p) => p.medicine_group !== pharmacyObj.medicine_group)
                  .map((p) => p.medicine_group)
                  .indexOf(t.uuid) < 0
            )}
            pharmacySource={{
              uniqueKey: pharmacyObj.uniqueKey,
              medicine_group: pharmacyObj.medicine_group,
              medicine_price_policy: pharmacyObj.medicine_price_policy,
              source: pharmacyObj.source,
            }}
            localSettingData={localSettingData}
            cbPharmacyChange={this.onChangePharmacyData}
            cbPharmacyRemove={this.onRemovePharmacyData}
          />
          {index < dataPharmacyList.length - 1 && !isCloud && <Divider style={{ margin: '4px 0 4px 8px' }} dashed={true} />}
        </div>
      );
    });

    return (
      <div style={{ backgroundColor: '#f5f5f5', padding: '16px 16px 16px 0', position: 'relative' }}>
        {/* 供应商（配药中心/代理商）下拉框 */}
        {isAgent && (
          <Row gutter={24} style={{ margin: 0 }}>
            <Col span={4} style={{ textAlign: 'right', padding: 0 }}>
              {isEdit && <span className="colon-red">*</span>}
              {I18N.get('med_supplier')}
              <span className="asterisk-black">:</span>
            </Col>
            <Col span={8} style={{ padding: 0 }} className={regionError && isEdit ? 'has-error' : ''}>
              {isOrderOnline && this.getOnlineSupplierSelect()}
              {!isOrderOnline && this.getOfflineSupplierSelect()}
            </Col>
            {isEdit && (
              <Col span={12} style={{ padding: 0, textAlign: 'right' }}>
                <Button type="primary" style={{ marginRight: '32px' }} onClick={this.onAddPharmacyData}>
                  {I18N.get('common_add')}
                  {I18N.get('unit_pharmacy')}
                </Button>
              </Col>
            )}
          </Row>
        )}
        {isAgent && regionError && (
          <div className="ant-form-explain">
            <Col offset={4} style={{ color: '#F5222D' }}>
              {I18N.get('err_supplier_region')}
            </Col>
          </div>
        )}
        {/* 药库和价格模式配置 */}
        {pharmacySelectList}
        {/* 删除按钮 */}
        {isEdit && isAgent && (
          <div style={{ position: 'absolute', top: 0, right: 0, height: '32px', width: '32px', background: '#fff' }}>
            <Icon className="setting-delete-button" type="delete" onClick={this.onRemoveDrugStore} />
          </div>
        )}
      </div>
    );
  }
}

export default PharmacySelectBox;



// WEBPACK FOOTER //
// ./src/components/common/PharmacySelectBox.tsx